import {useEffect, useRef} from 'react'
import type {GenericContent, GenericCTAGroup} from '../../../../../../brand/lib/types/contentful'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'

import {Text} from '@primer/react-brand'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Label} from '../../_shared/Label/Label'
import {Spacer} from '../../_shared/Spacer/Spacer'

import Styles from './Overview.module.css'

type Props = {
  content: GenericContent
  index: number
  location: string
}

export function Overview(props: Props) {
  const {content, index, location} = props
  const {htmlId, label, text, ctas} = content.fields

  const direction = htmlId // Used for setting left/right alignment

  const eyebrow = label?.fields.text
  const description = text ? documentToPlainTextString(text) : ''
  const ctaGroup = ctas?.at(0) as GenericCTAGroup
  const list = (content.fields.content as GenericContent[] | undefined) ?? []

  // Animation
  const wrapperRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements: AnimationElement[] = []
    if (descriptionRef.current) elements.push({element: descriptionRef.current})
    if (ctaRef.current) elements.push({element: ctaRef.current})
    if (wrapperRef.current) {
      return animateFadeIn(elements, wrapperRef.current)
    }
  }, [])

  return (
    <div className={Styles.Overview} data-direction={direction || (index % 2 === 0 ? 'left' : 'right')}>
      <div className={Styles.Cell}>
        {eyebrow && <Label text={eyebrow} />}

        {list.length > 0 && (
          <>
            <Spacer size="64px" />
            <ul className={Styles.List}>
              {list.map(item => (
                <li key={item.sys.id} className={Styles.ListItem}>
                  {item.fields.heading}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className={Styles.Cell} ref={wrapperRef}>
        <div>
          {description && (
            <div ref={descriptionRef}>
              <Text as="p" className={Styles.Description}>
                {description}
              </Text>
            </div>
          )}

          {ctas && (
            <>
              <Spacer size="32px" size1280="64px" />
              <div ref={ctaRef}>
                <CtaButtons contentfulContent={ctaGroup} location={location} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

try{ Overview.displayName ||= 'Overview' } catch {}